// src/pages/agent-booking-system-sabre-integration/components/SearchParameters.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const SearchParameters = ({ searchParams, onSearch, loading }) => {
  const [params, setParams] = useState(searchParams);
  const [advancedOptions, setAdvancedOptions] = useState(false);

  const handleInputChange = (field, value) => {
    setParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSearch?.(params);
  };

  const clearSearch = () => {
    const clearedParams = {
      origin: '',
      destination: '',
      departureDate: '',
      returnDate: '',
      passengers: 1,
      class: 'economy',
      tripType: 'roundtrip'
    };
    setParams(clearedParams);
  };

  const airportCodes = [
    { value: 'LAX', label: 'Los Angeles (LAX)' },
    { value: 'JFK', label: 'New York JFK (JFK)' },
    { value: 'LHR', label: 'London Heathrow (LHR)' },
    { value: 'DXB', label: 'Dubai (DXB)' },
    { value: 'SIN', label: 'Singapore (SIN)' },
    { value: 'NRT', label: 'Tokyo Narita (NRT)' },
    { value: 'CDG', label: 'Paris CDG (CDG)' },
    { value: 'FRA', label: 'Frankfurt (FRA)' }
  ];

  const cabinClasses = [
    { value: 'economy', label: 'Economy' },
    { value: 'premium_economy', label: 'Premium Economy' },
    { value: 'business', label: 'Business' },
    { value: 'first', label: 'First Class' }
  ];

  const passengerOptions = Array.from({ length: 9 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1} ${i === 0 ? 'Passenger' : 'Passengers'}`
  }));

  return (
    <div className="w-96 bg-surface border-r border-border p-6 overflow-y-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-heading-bold text-text-primary mb-2">
          Search Parameters
        </h2>
        <p className="text-text-secondary text-sm">
          Use corporate negotiated rates and preferred suppliers
        </p>
      </div>
      {/* Search Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Trip Type */}
        <div>
          <label className="block text-sm font-body-medium text-text-primary mb-2">
            Trip Type
          </label>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => handleInputChange('tripType', 'roundtrip')}
              className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                params?.tripType === 'roundtrip' ?'bg-primary-50 border-primary-200 text-primary-700' :'bg-background border-border text-text-secondary hover:border-border-hover'
              }`}
            >
              Round Trip
            </button>
            <button
              type="button"
              onClick={() => handleInputChange('tripType', 'oneway')}
              className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                params?.tripType === 'oneway' ?'bg-primary-50 border-primary-200 text-primary-700' :'bg-background border-border text-text-secondary hover:border-border-hover'
              }`}
            >
              One Way
            </button>
          </div>
        </div>

        {/* Origin */}
        <div>
          <label className="block text-sm font-body-medium text-text-primary mb-2">
            From
          </label>
          <Select
            value={params?.origin}
            onChange={(value) => handleInputChange('origin', value)}
            options={airportCodes}
            placeholder="Select origin airport"
            required
          />
        </div>

        {/* Destination */}
        <div>
          <label className="block text-sm font-body-medium text-text-primary mb-2">
            To
          </label>
          <Select
            value={params?.destination}
            onChange={(value) => handleInputChange('destination', value)}
            options={airportCodes}
            placeholder="Select destination airport"
            required
          />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Departure Date
            </label>
            <Input
              type="date"
              value={params?.departureDate}
              onChange={(e) => handleInputChange('departureDate', e?.target?.value)}
              min={new Date()?.toISOString()?.split('T')?.[0]}
              required
            />
          </div>
          
          {params?.tripType === 'roundtrip' && (
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-2">
                Return Date
              </label>
              <Input
                type="date"
                value={params?.returnDate}
                onChange={(e) => handleInputChange('returnDate', e?.target?.value)}
                min={params?.departureDate || new Date()?.toISOString()?.split('T')?.[0]}
              />
            </div>
          )}
        </div>

        {/* Passengers */}
        <div>
          <label className="block text-sm font-body-medium text-text-primary mb-2">
            Passengers
          </label>
          <Select
            value={params?.passengers}
            onChange={(value) => handleInputChange('passengers', value)}
            options={passengerOptions}
          />
        </div>

        {/* Cabin Class */}
        <div>
          <label className="block text-sm font-body-medium text-text-primary mb-2">
            Cabin Class
          </label>
          <Select
            value={params?.class}
            onChange={(value) => handleInputChange('class', value)}
            options={cabinClasses}
          />
        </div>

        {/* Advanced Options */}
        <div>
          <button
            type="button"
            onClick={() => setAdvancedOptions(!advancedOptions)}
            className="flex items-center space-x-2 text-sm text-primary-600 hover:text-primary-700 font-body-medium"
          >
            <Icon 
              name={advancedOptions ? 'ChevronUp' : 'ChevronDown'} 
              size={16} 
            />
            <span>Advanced Options</span>
          </button>
        </div>

        {advancedOptions && (
          <div className="space-y-4 pt-4 border-t border-border">
            {/* Corporate Rate Access */}
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={params?.corporateRates || false}
                  onChange={(e) => handleInputChange('corporateRates', e?.target?.checked)}
                  className="w-4 h-4 text-primary-600 rounded border-border focus:ring-primary-500"
                />
                <span className="text-sm text-text-primary">
                  Include corporate negotiated rates
                </span>
              </label>
            </div>

            {/* Preferred Airlines */}
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-2">
                Preferred Airlines
              </label>
              <Input
                type="text"
                placeholder="AA, DL, UA (comma separated)"
                value={params?.preferredAirlines || ''}
                onChange={(e) => handleInputChange('preferredAirlines', e?.target?.value)}
              />
            </div>

            {/* Flexible Dates */}
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={params?.flexibleDates || false}
                  onChange={(e) => handleInputChange('flexibleDates', e?.target?.checked)}
                  className="w-4 h-4 text-primary-600 rounded border-border focus:ring-primary-500"
                />
                <span className="text-sm text-text-primary">
                  Search flexible dates (±3 days)
                </span>
              </label>
            </div>

            {/* Direct Flights Only */}
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={params?.directFlightsOnly || false}
                  onChange={(e) => handleInputChange('directFlightsOnly', e?.target?.checked)}
                  className="w-4 h-4 text-primary-600 rounded border-border focus:ring-primary-500"
                />
                <span className="text-sm text-text-primary">
                  Direct flights only
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3 pt-6">
          <Button
            type="submit"
            className="w-full"
            loading={loading}
            disabled={!params?.origin || !params?.destination || !params?.departureDate}
          >
            <Icon name="Search" size={16} className="mr-2" />
            Search Flights & Hotels
          </Button>
          
          <button
            type="button"
            onClick={clearSearch}
            className="w-full px-4 py-2 text-sm font-body-medium text-text-secondary border border-border rounded-lg hover:bg-secondary-50 transition-colors"
          >
            Clear Search
          </button>
        </div>
      </form>
      {/* Corporate Policy Info */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-body-medium text-blue-900 mb-1">
              Corporate Policy
            </h4>
            <p className="text-xs text-blue-700">
              Search results will prioritize policy-compliant options and highlight any restrictions.
            </p>
          </div>
        </div>
      </div>
      {/* Recent Searches */}
      <div className="mt-6">
        <h4 className="text-sm font-body-medium text-text-primary mb-3">
          Recent Searches
        </h4>
        <div className="space-y-2">
          {[
            { origin: 'LAX', destination: 'JFK', date: '2025-01-20' },
            { origin: 'SFO', destination: 'LHR', date: '2025-01-25' }
          ]?.map((search, index) => (
            <button
              key={index}
              onClick={() => setParams(prev => ({
                ...prev,
                origin: search?.origin,
                destination: search?.destination,
                departureDate: search?.date
              }))}
              className="w-full p-3 text-left bg-background border border-border rounded-lg hover:bg-secondary-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-body-medium text-text-primary">
                  {search?.origin} → {search?.destination}
                </span>
                <span className="text-xs text-text-secondary">
                  {new Date(search.date)?.toLocaleDateString()}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchParameters;